import React, { useState, useEffect } from 'react';
import { MdBlock, MdCheckCircle, MdDelete } from 'react-icons/md';
import Loader from '../../components/Loader';
import Navbar from './Navbar';
import { useParams, useNavigate } from 'react-router-dom';
import Calling from '../../api/Calling';
import { toast } from 'react-toastify';
import formatDateTime from '../../components/formatDateTime';
import generateCaptcha from '../../components/captcha';
import NotFound from '../../components/ul/NotFound';
import { imageType, invalidImageMessage, MAX_FILE_SIZE_IMAGE, imageSizeExceededMessage } from '../../components/imageType';
import { FaEye } from "react-icons/fa";
import AdsDataDisplay from '../../components/AdsDataDisplay';
import { FiMessageCircle } from 'react-icons/fi';
import { QRCodeSVG } from 'qrcode.react';

// Utility function to format seconds to HH:MM:SS
const formatTime = (seconds) => {
    if (!seconds || isNaN(seconds)) return '00:00:00';

    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = Math.floor(seconds % 60);

    return [
        hrs.toString().padStart(2, '0'),
        mins.toString().padStart(2, '0'),
        secs.toString().padStart(2, '0')
    ].join(':');
};

const UserInfo = () => {
    const navigate = useNavigate();
    const [loader, setLoader] = useState(false);
    const [userData, setUserData] = useState(null);
    const [quizHistory, setQuizHistory] = useState([]);
    const [withdrawHistory, setWithdrawHistory] = useState([]);
    const [pagination, setPagination] = useState({
        totalItems: 0,
        totalPages: 0,
        currentPage: 1,
        limit: 20,
    });
    const [withdrawPagination, setWithdrawPagination] = useState({
        totalItems: 0,
        totalPages: 0,
        currentPage: 1,
        limit: 20,
    });
    const [isEditing, setIsEditing] = useState(false);
    const [activeTab, setActiveTab] = useState('quiz'); // 'quiz' or 'withdraw'
    const [couponCode, setCouponCode] = useState('');
    const [couponExp, setCouponExp] = useState('');
    const [showAcceptModal, setShowAcceptModal] = useState(false);
    const [selectedWithdrawId, setSelectedWithdrawId] = useState(null);
    const [showDeductCoinsModal, setShowDeductCoinsModal] = useState(false);
    const [coinsToDeduct, setCoinsToDeduct] = useState('');
    const [isDeleteRequest, setIsDeleteRequest] = useState(false);
    const [showDeleteConfirmModal, setShowDeleteConfirmModal] = useState(false);
    const [deductionReason, setDeductionReason] = useState('');

    // New state variables for reject modal functionality
    const [selectedRequests, setSelectedRequests] = useState([]);
    const defaultRejectReason = "Your redemption request has been rejected by the administrator.";
    const [rejectReason, setRejectReason] = useState(defaultRejectReason);
    const [showRejectModal, setShowRejectModal] = useState(false);
    const [selectAll, setSelectAll] = useState(false);

    // Add new state variables for UPI handling
    const [upiImage, setUpiImage] = useState(null);
    const [imagePreview, setImagePreview] = useState(null);
    const [acceptError, setAcceptError] = useState('');
    const [showImageViewer, setShowImageViewer] = useState(false);
    const [viewerImageUrl, setViewerImageUrl] = useState('');

    const { id } = useParams();

    // Generate UPI URL function
    const generateUpiUrl = (upiId, amount, note = 'Payment') => {
        // Format according to UPI deep linking specification
        return `upi://pay?pa=${encodeURIComponent(upiId)}&pn=Recipient&am=${encodeURIComponent(amount)}&cu=INR&tn=${encodeURIComponent(note)}`;
    };

    // Calculate default expiration date (30 days from now)
    const getDefaultExpirationDate = () => {
        const date = new Date();
        date.setDate(date.getDate() + 30);
        return date.toISOString().split('T')[0]; // Format as YYYY-MM-DD
    };

    // Set default expiration date when opening modal
    useEffect(() => {
        if (showAcceptModal) {
            setCouponExp(getDefaultExpirationDate());
        }
    }, [showAcceptModal]);

    // Fetch user data and quiz history
    const fetchUserData = async (page = 1, limit = 20) => {
        setLoader(true);
        try {
            const response = await Calling.userInfo(id, page, limit);
            console.log(response);
            if (response?.status) {
                setUserData(response?.user);
                setQuizHistory(response?.data);
                setPagination(response?.pagination);
                setIsDeleteRequest(response?.isDelete || false);
            } else {
                toast.error(response?.message || 'Failed to fetch user data.');
            }
        } catch (error) {
            console.error('Error fetching user data:', error);
            toast.error('An unexpected error occurred.');
        } finally {
            setLoader(false);
        }
    };

    // Add this function to handle image file selection
    const handleImageChange = (e) => {
        const file = e.target.files[0];

        if (!file) return;

        // Validate file type
        if (!imageType.includes(file.type)) {
            setAcceptError(invalidImageMessage);
            return;
        }

        // Validate file size
        if (file.size > MAX_FILE_SIZE_IMAGE) {
            setAcceptError(imageSizeExceededMessage);
            return;
        }

        // Clear any previous errors
        setAcceptError('');

        // Set the file for upload
        setUpiImage(file);

        // Create a preview
        const reader = new FileReader();
        reader.onloadend = () => {
            setImagePreview(reader.result);
        };
        reader.readAsDataURL(file);
    };

    // Fetch user's withdraw history
    const fetchWithdrawHistory = async (page = 1, limit = 10) => {
        setLoader(true);
        try {
            // Modified to fetch withdraw history for this specific user
            const resp = await Calling.getWithdrawHistory(page, limit, 'all', id);

            if (resp?.status) {
                setWithdrawHistory(resp?.data || []);
                setWithdrawPagination({
                    totalItems: resp?.totalRecord || 0,
                    totalPages: resp?.totalPage || 0,
                    currentPage: parseInt(resp?.page) || 1,
                    limit: parseInt(resp?.limit) || 10,
                });
            } else {
                toast.error(resp?.message || 'Failed to fetch Redeem history.');
            }
        } catch (error) {
            console.error('Error fetching Redeem history:', error);
            toast.error('An unexpected error occurred.');
        } finally {
            setLoader(false);
        }
    };

    // Fetch data (for refreshing after batch operations)
    const fetchData = () => {
        fetchWithdrawHistory(withdrawPagination?.currentPage, withdrawPagination?.limit);
    };

    useEffect(() => {
        fetchUserData(pagination?.currentPage, pagination?.limit);
        fetchWithdrawHistory(withdrawPagination?.currentPage, withdrawPagination?.limit);
        // eslint-disable-next-line
    }, [id]);

    // Handle pagination for quiz history
    const handlePageChange = (newPage) => {
        if (newPage > 0 && newPage <= pagination.totalPages) {
            fetchUserData(newPage, pagination?.limit);
        }
    };

    // Handle pagination for withdraw history
    const handleWithdrawPageChange = (newPage) => {
        if (newPage > 0 && newPage <= withdrawPagination.totalPages) {
            fetchWithdrawHistory(newPage, withdrawPagination?.limit);
        }
    };

    // Save changes
    const handleSaveChanges = async () => {
        setLoader(true);

        try {
            if (userData?.u_coin < 0 || !Number.isInteger(Number(userData?.u_coin))) {
                toast.error('Invalid coin value');
                return;
            }
            if (!userData?.u_name) {
                toast.error('Invalid name');
                return;
            } else if (userData?.u_name.length > 30) {
                toast.error('Name should be less than 30 characters');
                return;
            } else if (userData?.u_name.length < 3) {
                toast.error('Name should be at least 3 characters long');
                return;
            }

            const body = {
                u_name: userData?.u_name,
                u_email: userData?.u_email || null,
                u_device: userData?.u_device,
                u_coin: userData?.u_coin,
            };
            const response = await Calling.updateUserInfo(id, body);
            if (response?.status) {
                toast.success(response?.message || 'User updated successfully!');
                setIsEditing(false);
                fetchUserData(pagination.currentPage);
            } else {
                toast.error(response?.message || 'Failed to save changes.');
            }
        } catch (error) {
            console.error('Error saving changes:', error);
            toast.error('An unexpected error occurred.');
        } finally {
            setLoader(false);
        }
    };

    // Block/unblock user
    const handleBlockUser = async (id, status) => {
        setLoader(true);
        try {
            const response = await Calling.blockUser(id, status);
            if (response?.status) {
                toast.success(response?.message || 'User status updated!');
            } else {
                toast.error(response?.message || 'Failed to update user status.');
            }
        } catch (error) {
            console.error('Error updating user status:', error);
            toast.error('An unexpected error occurred.');
        } finally {
            setLoader(false);
            fetchUserData(pagination.currentPage);
        }
    };

    // Handle delete user request
    const handleDeleteUser = async () => {
        const content = `You are about to delete user account ${userData?._id}. To confirm, please solve this math problem.`;
        const check = generateCaptcha(content);

        if (!check) {
            toast.error("Captcha answer is incorrect", { autoClose: 2000 });
            return;
        }

        setLoader(true);
        try {
            const body = { id: [userData?._id] };
            const response = await Calling.deleteUserreq(body);

            if (response?.status) {
                toast.success(response?.message || 'User deleted successfully');
                // Redirect to user list after successful deletion
                navigate(-1);
            } else {
                toast.error(response?.message || 'Failed to delete user');
            }
        } catch (error) {
            console.error('Error deleting user:', error);
            toast.error('Error deleting user');
        } finally {
            setLoader(false);
            setShowDeleteConfirmModal(false);
        }
    };

    // Modify the openAcceptModal function to reset UPI image state
    const openAcceptModal = (id) => {
        setSelectedWithdrawId(id);
        setShowAcceptModal(true);
        setUpiImage(null);
        setImagePreview(null);
        setAcceptError('');
        setCouponCode('');
    };

    // Update the handleAcceptWithdraw function to handle UPI image uploads
    const handleAcceptWithdraw = async () => {
        // Get the selected withdraw request object
        const selectedRequest = withdrawHistory.find(item => item._id === selectedWithdrawId);

        if (!selectedRequest) {
            toast.error('No request selected');
            return;
        }

        // Check if this is a UPI request
        const isUpiRequest = selectedRequest.couponType === 'UPI';

        // Create FormData for the API
        const formData = new FormData();

        // Validate based on request type
        if (isUpiRequest) {
            if (!upiImage) {
                setAcceptError('Please upload a UPI payment screenshot.');
                return;
            }

            // Add the image to the FormData for UPI requests
            formData.append('image', upiImage);

        } else {
            // For non-UPI requests, validate coupon code and expiration date
            if (!couponCode.trim()) {
                setAcceptError('Please enter a coupon code');
                return;
            }

            if (!couponExp) {
                setAcceptError('Please select an expiration date');
                return;
            }

            // Add coupon code and expiration date to FormData
            formData.append('couponCode', couponCode);

            // Create a Date object from the selected date, then convert to ISO string
            const expDate = new Date(couponExp);
            // Set the time to the end of day (23:59:59.999)
            expDate.setHours(23, 59, 59, 999);
            formData.append('couponExp', expDate.toISOString());
        }

        setLoader(true);
        try {
            // Call the unified API endpoint
            const resp = await Calling.acceptWithdrawRequest(selectedWithdrawId, formData);

            if (resp?.status) {
                toast.success(resp?.message || 'Request accepted successfully');
                fetchWithdrawHistory(withdrawPagination.currentPage, withdrawPagination.limit);
                setShowAcceptModal(false);
                setCouponCode('');
                setCouponExp('');
                setUpiImage(null);
                setImagePreview(null);
                setAcceptError('');
            } else {
                toast.error(resp?.message || 'Failed to accept request');
            }
        } catch (e) {
            console.log(e);
            toast.error('Something went wrong');
        } finally {
            setLoader(false);
        }
    };

    // NEW FUNCTION: Handle batch reject withdraw requests
    const handleRejectRequests = async () => {
        if (selectedRequests.length === 0) {
            toast.error('Please select at least one request to reject');
            return;
        }

        // Use default reason if none provided
        const finalReason = rejectReason.trim() || defaultRejectReason;
        try {
            setLoader(true);
            // Modified to send rejection reason, admin info, and timestamp
            const resp = await Calling.rejectWithdrawRequests(
                selectedRequests,
                finalReason,
            );

            if (resp?.status) {
                toast.success(resp?.message || 'Requests rejected successfully');
                fetchData();
                setShowRejectModal(false);
                setSelectedRequests([]);
                setSelectAll(false);
                setRejectReason('');
            } else {
                toast.error(resp?.message || 'Failed to reject requests');
            }
        } catch (e) {
            console.log(e);
            toast.error('Something went wrong');
        } finally {
            setLoader(false);
        }
    };

    // Handle deduct coins
    const handleDeductCoins = async () => {
        // Validate input
        if (!coinsToDeduct || isNaN(coinsToDeduct) || Number(coinsToDeduct) <= 0) {
            toast.error('Please enter a valid positive number of coins to deduct');
            return;
        }

        if (!deductionReason.trim()) {
            toast.error('Please provide a reason for the deduction');
            return;
        }

        const coins = Number(coinsToDeduct);

        // Make sure user has enough coins
        if (userData?.u_coin < coins) {
            toast.error('User does not have enough coins');
            return;
        }

        setLoader(true);
        try {
            const response = await Calling.adminDeductCoins({
                userId: id,
                coins,
                reason: deductionReason.trim()
            });

            if (response?.status) {
                toast.success(response?.message || 'Coins deducted successfully');
                setShowDeductCoinsModal(false);
                setCoinsToDeduct('');
                setDeductionReason('');
                // Refresh user data and quiz history to show updated coin balance and new entry
                fetchUserData(pagination.currentPage);
            } else {
                toast.error(response?.message || 'Failed to deduct coins');
            }
        } catch (error) {
            console.error('Error deducting coins:', error);
            toast.error('An unexpected error occurred');
        } finally {
            setLoader(false);
        }
    };

    // Safely get a value with default
    const safeValue = (value, defaultValue = '') => {
        return value !== undefined && value !== null ? value : defaultValue;
    };

    // Updated getWithdrawCoinDisplay function in UserInfo component
    const getWithdrawCoinDisplay = (item) => {
        const coins = safeValue(item.coin, 0);
        const h_coins = safeValue(item.h_coins, 0);
        const trnType = safeValue(item.trnType, 'sub');

        if (trnType === 'add') {
            return (
                <div className="flex flex-col">
                    <span className="text-green-600 font-medium">+{coins}</span>
                    <span className="text-xs text-gray-500">Balance: {h_coins}</span>
                </div>
            );
        } else {
            return (
                <div className="flex flex-col">
                    <span className="text-red-600 font-medium">-{coins}</span>
                    <span className="text-xs text-gray-500">Balance: {h_coins}</span>
                </div>
            );
        }
    };


    // Delete Confirmation Modal
    const DeleteConfirmModal = () => {
        /* Delete confirmation modal implementation - unchanged from original */
        return (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                <div className="bg-white rounded-lg p-6 w-full max-w-md shadow-xl">
                    {/* Modal content - details omitted for brevity */}
                    <div className="flex justify-end space-x-3">
                        <button
                            onClick={() => setShowDeleteConfirmModal(false)}
                            className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-300"
                        >
                            Cancel
                        </button>
                        <button
                            onClick={handleDeleteUser}
                            className="px-4 py-2 text-white bg-red-600 rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 flex items-center"
                        >
                            <MdDelete className="mr-2" /> Delete Account
                        </button>
                    </div>
                </div>
            </div>
        );
    };

    // Function to handle chat click - navigate to chat page
    const handleChatClick = (chatId) => {
        // Simply navigate to the chat page
        // The getChatMessages API will automatically mark as read when admin views it
        navigate('/manage/contact/chat/read?id=' + chatId);
    };

    // Near the top of your AcceptModal section, add:
    const selectedRequest = withdrawHistory.find(item => item._id === selectedWithdrawId);

    return (
        <>
            {loader ? (
                <Loader loader={loader} />
            ) : (
                <div className="w-full text-main-font mt-16 px-3 md:ml-[4.4rem]">
                    <h1 className="text-xl md:text-2xl font-bold mt-4 text-main-font w-auto">User Management</h1>
                    <div className='bg-main-white rounded-xl'>
                        <div className='rounded-xl'>
                            <Navbar selectState={'user'} />
                            <hr className='border-main-border ' />

                            {/* Delete Request Banner */}
                            {isDeleteRequest && (
                                <div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4 mx-6 mt-4 rounded-r-lg shadow-md">
                                    <div className="flex items-center">
                                        <div className="bg-red-500 text-white p-2 rounded-full mr-3">
                                            <MdDelete className="text-xl" />
                                        </div>
                                        <div>
                                            <p className="font-semibold text-lg">Account Deletion Request</p>
                                            <p className="text-sm mt-1">
                                                This user has submitted a request to delete their account.
                                                Please review and process this request at your earliest convenience.
                                            </p>
                                        </div>
                                    </div>
                                    <div className="mt-3 flex justify-end">
                                        <button
                                            onClick={() => setShowDeleteConfirmModal(true)}
                                            className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition duration-200 flex items-center shadow-md"
                                        >
                                            <MdDelete className="mr-2" /> Process Deletion Request
                                        </button>
                                    </div>
                                </div>
                            )}

                            <div className='p-6'>
                                <div className='flex justify-between items-center mb-5'>
                                    <h1 className='text-2xl font-bold text-main-font'>
                                        User Info
                                    </h1>
                                </div>
                                <hr className='border-main-border' />
                                {/* User Info */}
                                <div className="my-6">
                                    {userData && (
                                        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                                            {/* User Header Section */}
                                            <div className="relative bg-gradient-to-r from-primary-blue to-primary-purple p-6 text-white">
                                                <div className="flex items-center gap-4">
                                                    <div className="relative">
                                                        {userData?.u_profile ? (
                                                            <img
                                                                src={userData.u_profile}
                                                                alt={userData.u_name}
                                                                className="w-20 h-20 rounded-full border-4 border-white shadow-md object-cover"
                                                            />
                                                        ) : (
                                                            <div className="w-20 h-20 rounded-full bg-white/30 flex items-center justify-center text-white text-2xl font-bold border-4 border-white">
                                                                {userData.u_name?.charAt(0)?.toUpperCase() || "U"}
                                                            </div>
                                                        )}
                                                        <span className={`absolute bottom-1 right-1 w-5 h-5 rounded-full border-2 border-white ${userData?.u_status ? 'bg-green-500' : 'bg-red-500'
                                                            }`}></span>
                                                    </div>
                                                    <div>
                                                        <h2 className="text-2xl font-bold">{userData.u_name || 'N/A'}</h2>
                                                        <p className="text-white/80">{userData.u_email || 'No email provided'}</p>
                                                        <div className="flex items-center gap-2 mt-1">
                                                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${userData?.u_status
                                                                ? 'bg-green-100 text-green-800'
                                                                : 'bg-red-100 text-red-800'
                                                                }`}>
                                                                {userData?.u_status ? 'Active' : 'Blocked'}
                                                            </span>
                                                            <span className="text-sm">
                                                                • Registered: {userData?.createdAt ? formatDateTime(userData.createdAt) : 'N/A'}
                                                            </span>
                                                        </div>
                                                    </div>
                                                </div>

                                                {/* User Stats */}
                                                <div className="absolute right-6 top-6 flex flex-row items-end gap-3">

                                                    {/* Coins Box */}
                                                    <div className="w-20 h-16 bg-white/15 backdrop-blur-md rounded-xl  shadow-lg flex flex-col justify-center items-center border border-white/20">
                                                        <div className="text-lg font-bold text-yellow-300 drop-shadow">
                                                            {userData?.u_coin || 0}
                                                        </div>
                                                        <div className="text-[10px] uppercase tracking-wide text-white/80">
                                                            Coins
                                                        </div>
                                                    </div>

                                                    {/* Chat Box */}
                                                    <button
                                                        onClick={() => handleChatClick(userData?._id)}
                                                        className="w-20 h-16 bg-white/15 backdrop-blur-md rounded-xl shadow-lg flex flex-col justify-center items-center border border-white/20 hover:bg-white/25 transition"
                                                    >
                                                        <FiMessageCircle size={20} className="text-white mb-2" />
                                                        <span className="text-[10px] uppercase tracking-wide text-white/80">
                                                            Chat
                                                        </span>
                                                    </button>
                                                </div>
                                            </div>

                                            {/* User Details Section */}
                                            <div className="p-6">
                                                <div className="flex justify-between items-center mb-4">
                                                    <h3 className="text-lg font-semibold text-gray-800">Account Details</h3>
                                                    {!isEditing ? (
                                                        <button
                                                            onClick={() => setIsEditing(true)}
                                                            className="text-primary-blue hover:text-primary-purple flex items-center gap-1 text-sm font-medium"
                                                        >
                                                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                                                            </svg>
                                                            Edit
                                                        </button>
                                                    ) : null}
                                                </div>

                                                {isEditing ? (
                                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                                        <div className="space-y-4">
                                                            <div>
                                                                <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                                                                <input
                                                                    type="text"
                                                                    value={userData.u_name}
                                                                    onChange={(e) => setUserData({ ...userData, u_name: e.target.value })}
                                                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-blue focus:border-transparent"
                                                                />
                                                            </div>
                                                            <div>
                                                                <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                                                                <input
                                                                    type="email"
                                                                    value={userData.u_email}
                                                                    onChange={(e) => setUserData({ ...userData, u_email: e.target.value })}
                                                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-blue focus:border-transparent"
                                                                />
                                                            </div>
                                                        </div>
                                                        <div className="space-y-4">
                                                            <div>
                                                                <label className="block text-sm font-medium text-gray-700 mb-1">Coins</label>
                                                                <input
                                                                    type="number"
                                                                    value={userData.u_coin}
                                                                    onChange={(e) => setUserData({ ...userData, u_coin: e.target.value })}
                                                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-blue focus:border-transparent"
                                                                />
                                                            </div>
                                                            <div>
                                                                <label className="block text-sm font-medium text-gray-700 mb-1">Device ID</label>
                                                                <input
                                                                    type="text"
                                                                    value={userData.u_device}
                                                                    onChange={(e) => setUserData({ ...userData, u_device: e.target.value })}
                                                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-blue focus:border-transparent"
                                                                    disabled
                                                                />
                                                                <p className="mt-1 text-xs text-gray-500">Device ID cannot be changed</p>
                                                            </div>
                                                        </div>
                                                        <div className="col-span-1 md:col-span-2 flex justify-end gap-3 mt-4">
                                                            <button
                                                                onClick={() => setIsEditing(false)}
                                                                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                                                            >
                                                                Cancel
                                                            </button>
                                                            <button
                                                                onClick={handleSaveChanges}
                                                                className="px-4 py-2 bg-primary-blue text-white rounded-lg hover:bg-primary-purple"
                                                            >
                                                                Save Changes
                                                            </button>
                                                        </div>
                                                    </div>
                                                ) : (
                                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                                        <div className="space-y-4">
                                                            <div>
                                                                <div className="text-sm text-gray-500">Name</div>
                                                                <div className="text-gray-900 font-medium">{userData.u_name || 'N/A'}</div>
                                                            </div>
                                                            <div>
                                                                <div className="text-sm text-gray-500">Email</div>
                                                                <div className="text-gray-900 font-medium">{userData.u_email || 'N/A'}</div>
                                                            </div>
                                                        </div>
                                                        <div className="space-y-4">
                                                            <div>
                                                                <div className="text-sm text-gray-500">Device ID</div>
                                                                <div className="text-gray-900 font-mono text-sm bg-gray-100 p-2 rounded">{userData.u_device || 'N/A'}</div>
                                                            </div>
                                                            <div>
                                                                <div className="text-sm text-gray-500">Registered Date</div>
                                                                <div className="text-gray-900 font-medium">{userData?.createdAt ? formatDateTime(userData.createdAt) : 'N/A'}</div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                )}

                                                <div className="mt-8 pt-6 border-t border-gray-200">
                                                    <h3 className="text-lg font-semibold text-gray-800 mb-4">Account Management</h3>

                                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                                        {/* Account Status Card */}
                                                        <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                                                            <div className="flex justify-between items-start">
                                                                <div>
                                                                    <h4 className="font-medium text-gray-900">Account Status</h4>
                                                                    <p className="text-sm text-gray-500 mt-1">Change the user's ability to access the platform</p>
                                                                </div>
                                                                <div className={`flex items-center justify-center h-10 w-10 rounded-full ${userData?.u_status ? 'bg-green-100 text-green-600' : 'bg-red-100 text-red-600'
                                                                    }`}>
                                                                    {userData?.u_status ? (
                                                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                                                        </svg>
                                                                    ) : (
                                                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
                                                                        </svg>
                                                                    )}
                                                                </div>
                                                            </div>

                                                            <div className="mt-4">
                                                                <div className="flex items-center">
                                                                    <div className="relative inline-block w-10 mr-2 align-middle select-none">
                                                                        <input
                                                                            type="checkbox"
                                                                            checked={userData?.u_status}
                                                                            onChange={() => handleBlockUser(userData?._id, !userData?.u_status)}
                                                                            className="opacity-0 w-0 h-0 absolute"
                                                                            id="toggle-status"
                                                                        />
                                                                        <label
                                                                            htmlFor="toggle-status"
                                                                            className={`block overflow-hidden h-6 rounded-full cursor-pointer transition-colors duration-200 ease-in-out ${userData?.u_status ? 'bg-green-500' : 'bg-red-500'
                                                                                }`}
                                                                        >
                                                                            <span
                                                                                className={`block h-6 w-6 rounded-full bg-white transform transition-transform duration-200 ease-in-out ${userData?.u_status ? 'translate-x-4' : 'translate-x-0'
                                                                                    }`}
                                                                            ></span>
                                                                        </label>
                                                                    </div>
                                                                    <div className="ml-2 text-sm font-medium">
                                                                        {userData?.u_status
                                                                            ? <span className="text-green-700">Active</span>
                                                                            : <span className="text-red-700">Blocked</span>
                                                                        }
                                                                    </div>
                                                                </div>
                                                                <p className="mt-2 text-xs text-gray-500">
                                                                    {userData?.u_status
                                                                        ? "User has full access to the platform"
                                                                        : "User is blocked from accessing the platform"
                                                                    }
                                                                </p>
                                                            </div>
                                                        </div>

                                                        {/* Coin Management Card */}
                                                        <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                                                            <div className="flex justify-between items-start">
                                                                <div>
                                                                    <h4 className="font-medium text-gray-900">Coin Balance</h4>
                                                                    <p className="text-sm text-gray-500 mt-1">Manage user's coin balance</p>
                                                                </div>
                                                                <div className="flex items-center justify-center h-10 w-10 rounded-full bg-yellow-100 text-yellow-600">
                                                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                                                    </svg>
                                                                </div>
                                                            </div>

                                                            <div className="mt-4">
                                                                <div className="text-2xl font-bold text-gray-900">{userData.u_coin || 0}</div>
                                                                <p className="text-sm text-gray-500">Current coin balance</p>

                                                                <button
                                                                    onClick={() => setShowDeductCoinsModal(true)}
                                                                    className="mt-3 w-full px-4 py-2 bg-yellow-500 text-white rounded-lg hover:bg-yellow-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                                                                    disabled={!userData?._id || (userData?.u_coin || 0) <= 0}
                                                                >
                                                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                                                                        <path d="M3 10a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z" />
                                                                    </svg>
                                                                    Deduct Coins
                                                                </button>
                                                            </div>
                                                        </div>

                                                        {/* Delete Account Card */}
                                                        <div className="md:col-span-2 bg-red-50 rounded-lg p-4 border border-red-200">
                                                            <div className="flex justify-between items-start">
                                                                <div>
                                                                    <h4 className="font-medium text-red-900">Danger Zone</h4>
                                                                    <p className="text-sm text-red-700 mt-1">Permanently delete this user account</p>
                                                                </div>
                                                                <div className="flex items-center justify-center h-10 w-10 rounded-full bg-red-100 text-red-600">
                                                                    <MdDelete className="h-6 w-6" />
                                                                </div>
                                                            </div>

                                                            <div className="mt-4 flex justify-end">
                                                                <button
                                                                    onClick={() => setShowDeleteConfirmModal(true)}
                                                                    className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                                                                    disabled={!userData?._id}
                                                                >
                                                                    <MdDelete className="h-5 w-5" />
                                                                    Delete Account
                                                                </button>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>

                                {/* Tab Selector */}
                                <div className="my-2 py-2 border-b border-main-border">
                                    <div className="flex space-x-4">
                                        <button
                                            onClick={() => setActiveTab('quiz')}
                                            className={`px-4 py-2 font-medium text-sm rounded-full ${activeTab === 'quiz'
                                                ? 'bg-blue-100 text-blue-800'
                                                : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                                                }`}
                                        >
                                            Quiz History
                                        </button>
                                        <button
                                            onClick={() => setActiveTab('withdraw')}
                                            className={`px-4 py-2 font-medium text-sm rounded-full ${activeTab === 'withdraw'
                                                ? 'bg-blue-100 text-blue-800'
                                                : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                                                }`}
                                        >
                                            Redeem History
                                        </button>
                                        <button
                                            onClick={() => setActiveTab('ads')}
                                            className={`px-4 py-2 font-medium text-sm rounded-full ${activeTab === 'ads'
                                                ? 'bg-blue-100 text-blue-800'
                                                : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                                                }`}
                                        >
                                            Ads Data
                                        </button>
                                    </div>
                                </div>

                                {/* Quiz History Section */}
                                {activeTab === 'quiz' && (
                                    <div className="mt-6">
                                        <h2 className="text-xl font-bold mb-4">Quiz History</h2>
                                        <hr className="border-main-border mb-6" />

                                        {quizHistory.length > 0 ? (
                                            <>
                                                <div className="overflow-x-auto">
                                                    <table className="min-w-full bg-white rounded-lg overflow-hidden">
                                                        <thead className="bg-gray-100">
                                                            <tr>
                                                                <th className="py-3 px-4 text-left">Quiz Name</th>
                                                                <th className="py-3 px-4 text-left">Coins</th>
                                                                <th className="py-3 px-4 text-left">Rank</th>
                                                                <th className="py-3 px-4 text-left">Type</th>
                                                                <th className="py-3 px-4 text-left">Total Questions</th>
                                                                <th className="py-3 px-4 text-left">Correct Answers</th>
                                                                <th className="py-3 px-4 text-left">Time Taken</th>
                                                                <th className="py-3 px-4 text-left">Date</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {quizHistory.map((quiz) => (
                                                                <tr key={quiz._id} className="border-b border-gray-200 hover:bg-gray-50">
                                                                    <td className="py-3 px-4">{quiz.quizName || '-'}</td>
                                                                    <td className="py-3 px-4">
                                                                        <div className="flex flex-col">
                                                                            <span className={`font-medium ${quiz.trnType === 'add' ? 'text-green-600' : 'text-red-600'}`}>
                                                                                {quiz.trnType === 'add' ? '+' : '-'}{safeValue(quiz.coins, 0)}
                                                                            </span>
                                                                            <span className="text-xs text-gray-500">Balance: {safeValue(quiz.h_coins, 0)}</span>
                                                                        </div>
                                                                    </td>
                                                                    <td className="py-3 px-4">{quiz.rank || 0}</td>
                                                                    <td className="py-3 px-4 capitalize">{quiz.type || '-'}</td>
                                                                    <td className="py-3 px-4">{safeValue(quiz.totalQuestion, 0)}</td>
                                                                    <td className="py-3 px-4">
                                                                        <span className={`px-2 py-1 rounded-full text-xs ${safeValue(quiz.correctAns, 0) === safeValue(quiz.totalQuestion, 0) ?
                                                                            'bg-green-100 text-green-800' :
                                                                            safeValue(quiz.correctAns, 0) > safeValue(quiz.totalQuestion, 0) / 2 ?
                                                                                'bg-blue-100 text-blue-800' :
                                                                                'bg-yellow-100 text-yellow-800'
                                                                            }`}>
                                                                            {safeValue(quiz.correctAns, 0)}
                                                                        </span>
                                                                    </td>
                                                                    <td className="py-3 px-4">
                                                                        <span className="font-mono text-sm">
                                                                            {formatTime(safeValue(quiz.timing, 0))}
                                                                        </span>
                                                                    </td>
                                                                    <td className="py-3 px-4">{formatDateTime(quiz.createdAt)}</td>
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                    </table>
                                                </div>

                                                {pagination.totalPages > 1 && (
                                                    <div className="flex justify-center mt-6">
                                                        <div className="flex space-x-2">
                                                            {/* Previous Button */}
                                                            <button
                                                                onClick={() => handlePageChange(pagination.currentPage - 1)}
                                                                disabled={pagination.currentPage === 1}
                                                                className={`px-4 py-2 rounded ${pagination.currentPage === 1
                                                                    ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                                                                    : 'bg-primary-blue text-white hover:bg-primary-purple'
                                                                    }`}
                                                            >
                                                                Previous
                                                            </button>

                                                            {/* Dynamic Page Numbers (max 5 shown) */}
                                                            {(() => {
                                                                const pages = [];
                                                                const totalPages = pagination.totalPages;
                                                                const currentPage = pagination.currentPage;

                                                                let startPage = Math.max(currentPage - 2, 1);
                                                                let endPage = Math.min(startPage + 4, totalPages);

                                                                // Adjust startPage if near end
                                                                if (endPage - startPage < 4) {
                                                                    startPage = Math.max(endPage - 4, 1);
                                                                }

                                                                for (let i = startPage; i <= endPage; i++) {
                                                                    pages.push(
                                                                        <button
                                                                            key={i}
                                                                            onClick={() => handlePageChange(i)}
                                                                            className={`px-4 py-2 rounded ${currentPage === i
                                                                                ? 'bg-primary-purple text-white'
                                                                                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                                                                                }`}
                                                                        >
                                                                            {i}
                                                                        </button>
                                                                    );
                                                                }

                                                                return pages;
                                                            })()}

                                                            {/* Next Button */}
                                                            <button
                                                                onClick={() => handlePageChange(pagination.currentPage + 1)}
                                                                disabled={pagination.currentPage === pagination.totalPages}
                                                                className={`px-4 py-2 rounded ${pagination.currentPage === pagination.totalPages
                                                                    ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                                                                    : 'bg-primary-blue text-white hover:bg-primary-purple'
                                                                    }`}
                                                            >
                                                                Next
                                                            </button>
                                                        </div>
                                                    </div>
                                                )}

                                            </>
                                        ) : (
                                            <div className="text-center py-8 bg-gray-50 rounded-lg shadow-inner">
                                                <NotFound />
                                                <p className="text-gray-600 mt-4">No quiz history found for this user</p>
                                            </div>
                                        )}
                                    </div>
                                )}

                                {/* Withdraw History Section */}
                                {activeTab === 'withdraw' && (
                                    <div className="mt-6">
                                        <h2 className="text-xl font-bold mb-4">Redeem History</h2>
                                        <hr className="border-main-border mb-6" />

                                        {withdrawHistory.length > 0 ? (
                                            <>
                                                <div className="overflow-x-auto">
                                                    <table className="min-w-full bg-white rounded-lg overflow-hidden">
                                                        <thead className="bg-gray-100">
                                                            <tr>
                                                                <th className="py-3 px-4 text-left">Amount</th>
                                                                <th className="py-3 px-4 text-left">Coins</th>
                                                                <th className="py-3 px-4 text-left">Coupon</th>
                                                                <th className="py-3 px-4 text-left">Status</th>
                                                                <th className="py-3 px-4 text-left">Reason</th>
                                                                <th className="py-3 px-4 text-left">Update</th>
                                                                <th className="py-3 px-4 text-left">Date</th>
                                                                <th className="py-3 px-4 text-left">Actions</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {withdrawHistory.map((item) => (
                                                                <tr key={item._id} className="border-b border-gray-200 hover:bg-gray-50">
                                                                    <td className="py-3 px-4">₹{safeValue(item.amount, 0)}</td>
                                                                    <td className="py-3 px-4">
                                                                        {getWithdrawCoinDisplay(item)}
                                                                    </td>
                                                                    <td className="py-3 px-4">
                                                                        <div className="flex items-center">
                                                                            {item.couponImage && (
                                                                                <div className="relative group">
                                                                                    <img
                                                                                        src={item.couponImage}
                                                                                        alt="Coupon"
                                                                                        className="h-8 w-8 mr-2 rounded object-cover"
                                                                                        onError={(e) => {
                                                                                            e.target.onerror = null;
                                                                                            e.target.src = "https://via.placeholder.com/32";
                                                                                        }}
                                                                                    />
                                                                                    {/* Enlarged image on hover */}
                                                                                    <div className="absolute hidden group-hover:block left-0 top-0 transform -translate-y-full z-10">
                                                                                        <img
                                                                                            src={item.couponImage}
                                                                                            alt="Coupon Preview"
                                                                                            className="w-48 h-auto shadow-lg rounded border-2 border-main-blue"
                                                                                            onError={(e) => {
                                                                                                e.target.onerror = null;
                                                                                                e.target.src = "https://via.placeholder.com/200";
                                                                                            }}
                                                                                        />
                                                                                    </div>
                                                                                </div>
                                                                            )}
                                                                            <span title={safeValue(item.couponTitle)} className="truncate max-w-[150px]">
                                                                                {safeValue(item.couponTitle)}
                                                                            </span>
                                                                        </div>
                                                                    </td>
                                                                    <td className="py-3 px-4">
                                                                        <span className={`px-2 py-1 text-xs font-semibold rounded-full 
                            ${item.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                                                                                item.status === 'completed' ? 'bg-green-100 text-green-800' :
                                                                                    item.status === 'rejected' ? 'bg-red-100 text-red-800' :
                                                                                        item.status === 'adminRejected' ? 'bg-orange-100 text-orange-800' :
                                                                                            'bg-gray-100 text-gray-800'}`}>
                                                                            {item.status === 'pending' ? 'Pending' :
                                                                                item.status === 'completed' ? 'Completed' :
                                                                                    item.status === 'rejected' ? 'Rejected' :
                                                                                        item.status === 'adminRejected' ? 'adminRejected' :
                                                                                            item.status}
                                                                        </span>
                                                                        {item.status === 'completed' && (
                                                                            <div className="mt-1 text-xs">
                                                                                <div className="text-gray-500">
                                                                                    {item.couponType === 'UPI' ? (
                                                                                        <div className="flex items-center">
                                                                                            {item.upi_image ? (
                                                                                                <button
                                                                                                    className="flex items-center gap-1 text-blue-600 hover:text-blue-800 transition-colors"
                                                                                                    title="View UPI Payment Screenshot"
                                                                                                    onClick={() => {
                                                                                                        setViewerImageUrl(item.upi_image);
                                                                                                        setShowImageViewer(true);
                                                                                                    }}
                                                                                                >
                                                                                                    <FaEye className="h-4 w-4" />

                                                                                                </button>
                                                                                            ) : (
                                                                                                <span className="text-gray-500 text-sm italic">No screenshot</span>
                                                                                            )}
                                                                                        </div>
                                                                                    ) : (
                                                                                        <>
                                                                                            Code: <span className="font-medium text-blue-600">{item.couponCode || '-'}</span>
                                                                                        </>
                                                                                    )}
                                                                                </div>
                                                                                {item.couponExp && !item.couponType === 'UPI' && (
                                                                                    <div className="text-gray-500">
                                                                                        Expires: <span className={`font-medium ${new Date(item.couponExp) < new Date() ? 'text-red-600' : 'text-green-600'}`}>
                                                                                            {formatDateTime(item.couponExp)}
                                                                                            {new Date(item.couponExp) < new Date() && ' (Expired)'}
                                                                                        </span>
                                                                                    </div>
                                                                                )}
                                                                            </div>
                                                                        )}
                                                                    </td>
                                                                    <td>{item.reason || '--'}</td>
                                                                    <td className="py-3 px-4 text-wrap">{formatDateTime(item?.updatedAt) || '--'}</td>
                                                                    <td className="py-3 px-4 text-wrap">{formatDateTime(item.createdAt)}</td>
                                                                    <td className="py-3 px-4">
                                                                        {item.status === 'pending' ? (
                                                                            <div className="flex space-x-2">
                                                                                <button
                                                                                    onClick={() => openAcceptModal(item._id)}
                                                                                    className="px-3 py-1 bg-primary-blue text-white text-sm rounded hover:bg-opacity-90"
                                                                                >
                                                                                    Accept
                                                                                </button>
                                                                                <button
                                                                                    onClick={() => {
                                                                                        setSelectedRequests([item._id]);
                                                                                        setShowRejectModal(true);
                                                                                    }}
                                                                                    className="px-3 py-1 bg-red-500 text-white text-sm rounded hover:bg-opacity-90"
                                                                                >
                                                                                    Reject
                                                                                </button>
                                                                            </div>
                                                                        ) : (
                                                                            <span className="text-gray-500 text-sm">Processed</span>
                                                                        )}
                                                                    </td>
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                    </table>
                                                </div>

                                                {withdrawPagination.totalPages > 1 && (
                                                    <div className="flex justify-center mt-6">
                                                        <div className="flex space-x-2">
                                                            {/* Previous Button */}
                                                            <button
                                                                onClick={() => handleWithdrawPageChange(withdrawPagination.currentPage - 1)}
                                                                disabled={withdrawPagination.currentPage === 1}
                                                                className={`px-4 py-2 rounded ${withdrawPagination.currentPage === 1
                                                                    ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                                                                    : 'bg-primary-blue text-white hover:bg-primary-purple'
                                                                    }`}
                                                            >
                                                                Previous
                                                            </button>

                                                            {/* Page Numbers - Max 5 */}
                                                            {(() => {
                                                                const pages = [];
                                                                const totalPages = withdrawPagination.totalPages;
                                                                const currentPage = withdrawPagination.currentPage;

                                                                let startPage = Math.max(currentPage - 2, 1);
                                                                let endPage = Math.min(startPage + 4, totalPages);

                                                                // Adjust start if endPage is near the end
                                                                if (endPage - startPage < 4) {
                                                                    startPage = Math.max(endPage - 4, 1);
                                                                }

                                                                for (let i = startPage; i <= endPage; i++) {
                                                                    pages.push(
                                                                        <button
                                                                            key={i}
                                                                            onClick={() => handleWithdrawPageChange(i)}
                                                                            className={`px-4 py-2 rounded ${withdrawPagination.currentPage === i
                                                                                ? 'bg-primary-purple text-white'
                                                                                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                                                                                }`}
                                                                        >
                                                                            {i}
                                                                        </button>
                                                                    );
                                                                }

                                                                return pages;
                                                            })()}

                                                            {/* Next Button */}
                                                            <button
                                                                onClick={() => handleWithdrawPageChange(withdrawPagination.currentPage + 1)}
                                                                disabled={withdrawPagination.currentPage === withdrawPagination.totalPages}
                                                                className={`px-4 py-2 rounded ${withdrawPagination.currentPage === withdrawPagination.totalPages
                                                                    ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                                                                    : 'bg-primary-blue text-white hover:bg-primary-purple'
                                                                    }`}
                                                            >
                                                                Next
                                                            </button>
                                                        </div>
                                                    </div>
                                                )}

                                            </>
                                        ) : (
                                            <div className="text-center py-8 bg-gray-50 rounded-lg shadow-inner">
                                                <NotFound />
                                                <p className="text-gray-600 mt-4">No Redeem history found for this user</p>
                                            </div>
                                        )}
                                    </div>
                                )}

                                {/* Ads Data Tab - Using the standalone component */}
                                {activeTab === 'ads' && (
                                    <AdsDataDisplay userId={id} />
                                )}

                            </div>
                        </div>
                    </div>
                </div>
            )}
            {/* Accept Modal */}
            {showAcceptModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
                    <div className="bg-white rounded-lg w-full max-w-md shadow-xl">
                        {/* Header */}
                        <div className="p-4 border-b border-gray-200">
                            <div className="flex items-center">
                                <div className="bg-blue-100 p-2 rounded-full mr-3 flex-shrink-0">
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-blue-600" viewBox="0 0 20 20" fill="currentColor">
                                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                    </svg>
                                </div>
                                <h3 className="text-lg font-bold text-gray-800">Accept {selectedRequest?.couponType === 'UPI' ? 'UPI ' : ''}Redeem Request</h3>
                            </div>
                        </div>

                        {/* Content */}
                        <div className="p-4 max-h-[70vh] overflow-y-auto">
                            {acceptError && (
                                <div className="mb-4 p-3 bg-red-100 text-red-700 rounded-md text-sm">
                                    {acceptError}
                                </div>
                            )}

                            <div className="space-y-4">
                                {selectedRequest?.couponType === 'UPI' ? (
                                    <div>
                                        <div className="flex justify-between items-center mb-2">
                                            <label className="block text-sm font-medium text-gray-700">
                                                UPI Payment Screenshot <span className="text-red-500">*</span>
                                            </label>
                                            <span className="text-xs text-gray-500">Max size: 1MB</span>
                                        </div>

                                        {imagePreview ? (
                                            <div className="relative mb-3">
                                                <img
                                                    src={imagePreview}
                                                    alt="Preview"
                                                    className="w-full h-auto max-h-40 object-contain border rounded-md"
                                                />
                                                <button
                                                    onClick={() => {
                                                        setUpiImage(null);
                                                        setImagePreview(null);
                                                    }}
                                                    className="absolute top-2 right-2 bg-red-600 text-white rounded-full p-1 shadow-md hover:bg-opacity-90"
                                                    title="Remove image"
                                                >
                                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                                                        <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                                                    </svg>
                                                </button>
                                            </div>
                                        ) : (
                                            <div className="border-2 border-dashed border-gray-300 rounded-md p-4 text-center hover:border-blue-500 transition-colors mb-3">
                                                <input
                                                    type="file"
                                                    id="upiImage"
                                                    onChange={handleImageChange}
                                                    className="hidden"
                                                    accept=".jpg,.jpeg,.png"
                                                />
                                                <label htmlFor="upiImage" className="cursor-pointer flex flex-col items-center">
                                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-gray-400 mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                                    </svg>
                                                    <span className="text-sm text-gray-700 font-medium">Click to upload image</span>
                                                    <span className="text-xs text-gray-500 mt-1">PNG, JPG, JPEG (Max 1MB)</span>
                                                </label>
                                            </div>
                                        )}

                                        <div className="bg-blue-50 border-l-4 border-blue-500 p-3 text-sm text-blue-700 mb-3">
                                            <p>Upload a screenshot of the UPI payment confirmation showing the transaction details.</p>
                                        </div>

                                        {selectedRequest?.upi_id && (
                                            <div className="mt-4">
                                                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-3">
                                                    <span className="text-sm font-medium text-gray-700">UPI ID:</span>
                                                    <div className="flex items-center">
                                                        <span className="text-sm text-gray-700 break-all mr-2 max-w-[180px] sm:max-w-none truncate sm:truncate-none">
                                                            {selectedRequest.upi_id}
                                                        </span>
                                                        <button
                                                            onClick={() => {
                                                                navigator.clipboard.writeText(selectedRequest.upi_id);
                                                                toast.success('UPI ID copied!');
                                                            }}
                                                            className="px-2 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700"
                                                            title="Copy UPI ID"
                                                        >
                                                            Copy
                                                        </button>
                                                    </div>
                                                </div>

                                                {/* QR Code Section */}
                                                <div className="bg-gray-50 p-3 rounded-md border border-gray-200">
                                                    <div className="flex flex-col items-center">
                                                        <div className="bg-white p-2 rounded-md shadow-sm mb-2 w-full max-w-[180px] mx-auto">
                                                            <QRCodeSVG
                                                                value={generateUpiUrl(selectedRequest.upi_id, selectedRequest.amount, 'Redeem Payment')}
                                                                size={180}
                                                                level="H"
                                                                includeMargin={true}
                                                                className="w-full h-auto"
                                                            />
                                                        </div>
                                                        <p className="text-xs text-center text-gray-500 mb-3 max-w-[250px]">
                                                            Scan with any UPI app to pay ₹{selectedRequest.amount} to {selectedRequest.upi_id}
                                                        </p>
                                                        <button
                                                            onClick={() => {
                                                                const upiUrl = generateUpiUrl(selectedRequest.upi_id, selectedRequest.amount, 'Redeem Payment');
                                                                window.open(upiUrl, '_blank');
                                                                toast.info('Opening UPI payment app...');
                                                            }}
                                                            className="w-full sm:w-auto px-4 py-2 bg-green-600 text-white text-sm rounded hover:bg-green-700 flex items-center justify-center"
                                                        >
                                                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                                                            </svg>
                                                            Pay Now
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                ) : (
                                    <>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                                Coupon Code <span className="text-red-500">*</span>
                                            </label>
                                            <input
                                                type="text"
                                                name="couponCode"
                                                value={couponCode}
                                                onChange={(e) => setCouponCode(e.target.value)}
                                                placeholder="Enter coupon code"
                                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                                autoFocus
                                            />
                                        </div>

                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                                Expiration Date <span className="text-red-500">*</span>
                                            </label>
                                            <input
                                                type="date"
                                                name="couponExp"
                                                value={couponExp}
                                                onChange={(e) => setCouponExp(e.target.value)}
                                                min={new Date().toISOString().split('T')[0]}
                                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                            />
                                            <p className="text-xs text-gray-500 mt-1">
                                                The coupon will expire at the end of this day (midnight).
                                            </p>
                                        </div>

                                        <div className="bg-blue-50 border-l-4 border-blue-500 p-3 text-sm text-blue-700">
                                            <p>By accepting this Redeem request, you confirm that the user will receive their requested funds.</p>
                                            <p className="mt-1">Please provide a valid coupon code to complete this transaction.</p>
                                        </div>
                                    </>
                                )}
                            </div>
                        </div>

                        {/* Footer/Actions */}
                        <div className="p-4 border-t border-gray-200">
                            <div className="flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-3 space-y-2 space-y-reverse sm:space-y-0">
                                <button
                                    onClick={() => {
                                        setShowAcceptModal(false);
                                        setCouponCode('');
                                        setCouponExp('');
                                        setUpiImage(null);
                                        setImagePreview(null);
                                        setAcceptError('');
                                    }}
                                    className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-300 w-full sm:w-auto"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handleAcceptWithdraw}
                                    className="px-4 py-2 text-white bg-blue-600 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-400 flex items-center justify-center w-full sm:w-auto"
                                    disabled={selectedRequest?.couponType === 'UPI' ? !upiImage : (!couponCode.trim() || !couponExp)}
                                >
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
                                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                    </svg>
                                    Accept
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}


            {/* Reject Modal */}
            {showRejectModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg p-6 w-full max-w-md">
                        <h3 className="text-lg font-medium text-main-font mb-4">Reject Redeem Request</h3>
                        <div className="space-y-4">
                            <p className="text-main-subfont">
                                Are you sure you want to reject {selectedRequests.length === 1 ? 'this' : 'these'} {selectedRequests.length} redeem request{selectedRequests.length !== 1 ? 's' : ''}?
                                The coins will be returned to the user's account.
                            </p>

                            <div>
                                <label className="block text-sm font-medium text-main-font mb-1">
                                    Rejection Reason
                                </label>
                                <textarea
                                    value={rejectReason}
                                    onChange={(e) => setRejectReason(e.target.value)}
                                    placeholder="Enter reason for rejection (optional)"
                                    className="w-full px-3 py-2 border border-main-border rounded-md focus:outline-none focus:ring-2 focus:ring-main-blue min-h-[80px]"
                                />
                                <p className="text-xs text-main-subfont mt-1">
                                    If left empty, a default reason will be used.
                                </p>
                            </div>
                        </div>
                        <div className="flex justify-end space-x-3 mt-6">
                            <button
                                onClick={() => {
                                    setShowRejectModal(false);
                                    setRejectReason('');
                                    if (selectedRequests.length === 1 && !selectAll) {
                                        setSelectedRequests([]);
                                    }
                                }}
                                className="px-4 py-2 text-main-font bg-white border border-main-border rounded-md hover:bg-main-th"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleRejectRequests}
                                className="px-4 py-2 text-white bg-main-delete rounded-md hover:bg-opacity-90"
                            >
                                Reject
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Deduct Coins Modal */}
            {showDeductCoinsModal &&
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg p-6 w-full max-w-md shadow-xl">
                        <div className="flex items-center mb-4 text-orange-600">
                            <div className="bg-orange-100 p-2 rounded-full mr-3">
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" viewBox="0 0 20 20" fill="currentColor">
                                    <path d="M8.433 7.418c.155-.103.346-.196.567-.267v1.698a2.305 2.305 0 01-.567-.267C8.07 8.34 8 8.114 8 8c0-.114.07-.34.433-.582zM11 12.849v-1.698c.22.071.412.164.567.267.364.243.433.468.433.582 0 .114-.07.34-.433.582a2.305 2.305 0 01-.567.267z" />
                                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-13a1 1 0 10-2 0v.092a4.535 4.535 0 00-1.676.662C6.602 6.234 6 7.009 6 8c0 .99.602 1.765 1.324 2.246.48.32 1.054.545 1.676.662v1.941c-.391-.127-.68-.317-.843-.504a1 1 0 10-1.51 1.31c.562.649 1.413 1.076 2.353 1.253V15a1 1 0 102 0v-.092a4.535 4.535 0 001.676-.662C13.398 13.766 14 12.991 14 12c0-.99-.602-1.765-1.324-2.246A4.535 4.535 0 0011 9.092V7.151c.391.127.68.317.843.504a1 1 0 101.511-1.31c-.563-.649-1.413-1.076-2.354-1.253V5z" clipRule="evenodd" />
                                </svg>
                            </div>
                            <h3 className="text-xl font-bold">Deduct Coins</h3>
                        </div>

                        <div className="bg-gray-50 p-4 rounded-lg border border-gray-200 mb-6">
                            <div className="flex items-center justify-between mb-4">
                                <span className="text-gray-700 font-medium">Current Balance:</span>
                                <span className="text-2xl font-bold text-orange-600">{userData?.u_coin || 0} coins</span>
                            </div>

                            <div className="mb-4">
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Amount to Deduct <span className="text-red-500">*</span>
                                </label>
                                <div className="relative">
                                    <input
                                        type="number"
                                        value={coinsToDeduct}
                                        onChange={(e) => setCoinsToDeduct(e.target.value)}
                                        placeholder="Enter coins to deduct"
                                        min="1"
                                        max={userData?.u_coin || 0}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 pr-12"
                                        autoFocus
                                    />
                                    <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                                        <span className="text-gray-500">coins</span>
                                    </div>
                                </div>
                            </div>

                            <div className="mb-4">
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Reason for Deduction <span className="text-red-500">*</span>
                                </label>
                                <textarea
                                    value={deductionReason}
                                    onChange={(e) => setDeductionReason(e.target.value)}
                                    placeholder="Explain why you're deducting these coins"
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                                    rows="3"
                                />
                                <p className="mt-1 text-xs text-gray-500">
                                    This reason will be recorded in the transaction history.
                                </p>
                            </div>

                            <div className="bg-orange-50 border-l-4 border-orange-500 p-3 text-sm text-orange-700">
                                <p>This will deduct coins from the user and create an entry in their transaction history.</p>
                                <p className="mt-1">New balance will be: <span className="font-bold">{(userData?.u_coin || 0) - (coinsToDeduct ? Number(coinsToDeduct) : 0)}</span> coins</p>
                            </div>
                        </div>

                        <div className="flex justify-end space-x-3">
                            <button
                                onClick={() => {
                                    setShowDeductCoinsModal(false);
                                    setCoinsToDeduct('');
                                    setDeductionReason('');
                                }}
                                className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-300"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleDeductCoins}
                                className="px-4 py-2 text-white bg-orange-500 rounded-md hover:bg-orange-600 focus:outline-none focus:ring-2 focus:ring-orange-400 flex items-center"
                                disabled={!coinsToDeduct || isNaN(coinsToDeduct) || Number(coinsToDeduct) <= 0 || Number(coinsToDeduct) > (userData?.u_coin || 0) || !deductionReason.trim()}
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
                                    <path fillRule="evenodd" d="M3 10a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z" clipRule="evenodd" />
                                </svg>
                                Deduct Coins
                            </button>
                        </div>
                    </div>
                </div>
            }

            {/* Image Viewer Modal */}
            {showImageViewer && (
                <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-75 z-50">
                    <div className="relative bg-white p-3 rounded-lg shadow-xl max-w-3xl w-full mx-4">
                        <img
                            src={viewerImageUrl}
                            alt="UPI Payment Screenshot"
                            className="w-full h-auto max-h-[80vh] object-contain rounded"
                            onError={(e) => {
                                e.target.onerror = null;
                                e.target.src = "https://via.placeholder.com/400?text=UPI+Screenshot";
                            }}
                        />
                        <button
                            onClick={() => setShowImageViewer(false)}
                            className="absolute top-2 right-2 bg-red-600 text-white rounded-full p-1.5 hover:bg-red-700 transition-colors shadow-md"
                            title="Close"
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>
                    </div>
                </div>
            )}

            {/* Delete Confirm Modal */}
            {showDeleteConfirmModal && <DeleteConfirmModal />}
        </>
    );
};

export default UserInfo;